import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, CreditCard, FileText, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import HeroCarousel from '../components/HeroCarousel';
import SEO from '../components/SEO';
import { parseMarkdownItalics } from '../components/SafecareBrand';
import homeData from '../data/home_content_v2.json';
import issuesData from '../data/issues_content.json';
import './Home.css';

export default function Home() {
    const featuredIssues = issuesData.issues?.slice(0, 4) ?? [];

    return (
        <div className="home-page">
            <SEO
                title="SAFEC+ - The Plan to Fix Healthcare"
                description="Universal coverage. Real prices. No more medical bankruptcy. A comprehensive legislative proposal for a modern nation."
                image="/image/home_hero.jpg"
            />
            {/* Animated Background Blobs */}
            <div className="blob-container">
                <motion.div
                    className="blob blob-1"
                    animate={{ x: [0, 50, -50, 0], y: [0, -50, 50, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                    className="blob blob-2"
                    animate={{ x: [0, -30, 30, 0], y: [0, 60, -60, 0] }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                />
            </div>

            {/* Hero Section */}
            <section className="hero">
                <div className="home-hero-bg-wrapper">
                    <img
                        className="home-hero-bg-img"
                        src="/image/home_hero_cinematic.jpg"
                        alt="Background"
                        loading="eager"
                        fetchPriority="high"
                    />
                    <div className="home-hero-overlay"></div>
                </div>

                <div className="container hero-container">
                    <motion.div
                        className="hero-content"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <motion.span
                            className="hero-badge"
                            whileHover={{ scale: 1.05 }}
                        >
                            <Heart size={16} fill="currentColor" /> {parseMarkdownItalics(homeData.hero.badge)}
                        </motion.span>

                        <h1 className="hero-title">
                            <img src="/image/safecarelogo.png" alt="SAFEC+" className="hero-logo-inline" width="76" height="76" /> {parseMarkdownItalics(homeData.hero.title_line1)} <span className="text-gradient">{homeData.hero.title_gradient}</span>
                        </h1>

                        <div className="hero-description-card">
                            <p className="hero-description">{parseMarkdownItalics(homeData.hero.description)}</p>
                        </div>

                        <div className="hero-benefits">
                            <motion.span className="hero-pill" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 400 }}>
                                <ShieldCheck size={18} /> Universal Coverage
                            </motion.span>
                            <motion.span className="hero-pill" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 400 }}>
                                <CreditCard size={18} /> Zero Deductibles
                            </motion.span>
                            <motion.span className="hero-pill" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 400 }}>
                                <FileText size={18} /> Full Legislative Draft
                            </motion.span>
                        </div>

                        <div className="hero-actions">
                            <Link to="/plan" className="btn btn-primary glow-effect">
                                {parseMarkdownItalics(homeData.hero.cta_primary)} <ArrowRight size={20} />
                            </Link>
                            <Link to="/myth-vs-reality" className="btn btn-glass">
                                {parseMarkdownItalics(homeData.hero.cta_secondary)}
                            </Link>
                        </div>

                        <div className="hero-trust-row">
                            <Link to="/the-bill" className="hero-trust-item">
                                <FileText size={18} /> Read the full bill
                            </Link>
                            <Link to="/calculator" className="hero-trust-item">
                                <CreditCard size={18} /> See your savings
                            </Link>
                            <Link to="/simulator" className="hero-trust-item">
                                <ShieldCheck size={18} /> National impact
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div
                        className="hero-visual"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <HeroCarousel slides={homeData.hero.carousel_slides} />
                    </motion.div>
                </div>

                {/* Key Numbers Strip — immediately below hero content */}
                {homeData.keyNumbers && (
                    <div className="key-numbers-strip">
                        <div className="container">
                            <div className="key-numbers-inner">
                                {homeData.keyNumbers.map((item, i) => (
                                    <motion.div
                                        key={item.value + i}
                                        className="key-number-card"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 0.5, delay: i * 0.08 }}
                                    >
                                        <span className="key-number-value">{item.value}</span>
                                        <span className="key-number-label">{item.label}</span>
                                        <span className="key-number-sublabel">{item.sublabel}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* The Problem — Dense stat cards + hook */}
            <section className="problem-section">
                <div className="container">
                    <motion.div
                        className="problem-hook"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="problem-badge">{parseMarkdownItalics(homeData.problem.title)}</span>
                        <h2 className="problem-subtitle">{parseMarkdownItalics(homeData.problem.subtitle)}</h2>
                        <div className="problem-text-card">
                            <p>{parseMarkdownItalics(homeData.problem.text)}</p>
                        </div>
                    </motion.div>

                    {homeData.problemStats && (
                        <div className="problem-stats-grid">
                            {homeData.problemStats.map((stat, i) => (
                                <motion.div
                                    key={stat.value + i}
                                    className="problem-stat-card"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true, margin: "-30px" }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                >
                                    <span className="problem-stat-value">{stat.value}</span>
                                    <span className="problem-stat-label">{stat.label}</span>
                                    <span className="problem-stat-sublabel">{stat.sublabel}</span>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Explore Tools */}
            {homeData.tools && (
                <section className="tools-section">
                    <div className="container">
                        <motion.div
                            className="section-header-accent"
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className="section-badge">Explore</span>
                            <h2 className="section-headline">Use the Tools</h2>
                            <p className="section-tagline">Run calculations. Read the bill. See how the system works.</p>
                        </motion.div>
                        <div className="tools-grid">
                            {homeData.tools.map((tool, i) => (
                                <motion.div
                                    key={tool.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                >
                                    <Link to={tool.path} className="tool-card">
                                        <div className="tool-card-image">
                                            <img src={tool.image} alt={tool.title} loading="lazy" />
                                            <div className="tool-card-overlay" />
                                        </div>
                                        <div className="tool-card-content">
                                            <span className="tool-sublabel">{tool.sublabel}</span>
                                            <h3>{tool.title}</h3>
                                            <span className="tool-cta">Open <ArrowRight size={16} /></span>
                                        </div>
                                        <div className="tool-card-accent" />
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 15 Issues Explained — Teaser */}
            <section className="issues-teaser-section">
                <div className="container">
                    <motion.div
                        className="section-header-accent"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="section-badge">Resources</span>
                        <h2 className="section-headline">15 Issues Explained</h2>
                        <p className="section-tagline">Plain talk on the biggest questions. No spin.</p>
                    </motion.div>
                    <div className="issues-teaser-grid">
                        {featuredIssues.map((issue, i) => (
                            <motion.div
                                key={issue.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                            >
                                <Link to={`/issues/${issue.id}`} className="issue-teaser-card">
                                    <div className="issue-teaser-image">
                                        <img src={issue.image} alt={issue.title} loading="lazy" />
                                        <span className="issue-category-badge">{issue.category}</span>
                                    </div>
                                    <div className="issue-teaser-content">
                                        <h4>{parseMarkdownItalics(issue.title)}</h4>
                                        <p>{parseMarkdownItalics(issue.summary)}</p>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                    <motion.div
                        className="issues-cta-row"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                    >
                        <Link to="/issues" className="btn btn-glass">
                            See all 15 issues <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* The Pillars Layout */}
            <section className="pillars-section">
                <div className="container">
                    <motion.div
                        className="section-header-accent pillars-header"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="section-badge">The Plan</span>
                        <h2 className="section-headline">Five Pillars</h2>
                    </motion.div>
                    {homeData.pillars.map((pillar, index) => (
                        <div key={pillar.id} className={`pillar-row ${index % 2 === 1 ? 'reverse' : ''}`}>
                            <motion.div
                                className={`pillar-content pillar-color-${pillar.color}`}
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.6 }}
                            >
                                <span className={`pillar-badge bg-${pillar.color}`}>{parseMarkdownItalics(pillar.subtitle)}</span>
                                <h2>{parseMarkdownItalics(pillar.title)}</h2>
                                <p>{parseMarkdownItalics(pillar.description)}</p>
                                <Link
                                    to={pillar.link}
                                    className={`btn-link text-${pillar.color}`}
                                    aria-label={`Learn more about ${pillar.title}`}
                                >
                                    Learn more <span className="sr-only">about {pillar.title}</span> <ArrowRight size={16} />
                                </Link>
                            </motion.div>
                            <motion.div
                                className="pillar-visual"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <div className="img-frame">
                                    <img
                                        src={pillar.image}
                                        alt={pillar.title}
                                        width="1024"
                                        height="648"
                                        style={{ width: '100%', height: 'auto' }}
                                    />
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Final CTA */}
            <section className="community-cta">
                <div className="container">
                    <motion.div
                        className="cta-box glass-panel"
                        whileHover={{ scale: 1.01 }}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className="cta-content">
                            <h3>{parseMarkdownItalics(homeData.cta.title)}</h3>
                            <h2 className="font-medical-bill cta-bill-line">
                                THIS IS <span className="correction-wrapper">
                                    <span className="correction-original">NOT A</span>
                                    <span className="correction-replacement">THE</span>
                                </span> BILL
                            </h2>
                            <p>{parseMarkdownItalics(homeData.cta.text)}</p>
                            <Link to="/the-bill" className="btn btn-primary">{parseMarkdownItalics(homeData.cta.button)} <ArrowRight size={20} /></Link>
                        </div>
                        <div className="cta-image">
                            <img src={homeData.cta.image} alt="Legislation" />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
